import { type Dispatch, type Middleware, isAction } from '@reduxjs/toolkit';
import type { createApi } from '@reduxjs/toolkit/query';
import {
    createNormalizer,
    type Data,
    type NormalizerConfig,
} from '@normy/core'

export { getId } from '@normy/core';

export const getNormalizer = (
    dispatch: Dispatch,
): ReturnType<typeof createNormalizer> =>
    dispatch({ type: 'getNormalization' }) as unknown as ReturnType<
        typeof createNormalizer
    >;

export const createNormalizationMiddleware = (
    prefix: string,
    api: ReturnType<typeof createApi>,
    normalizerConfig?: Omit<NormalizerConfig, 'structuralSharing'> & {
        normalizeQuery?: (queryType: string) => boolean;
        normalizeMutation?: (mutationEndpointName: string) => boolean;
    },
): [any, Middleware] => {

    const executeQueryFulfilledType = `${prefix}/executeQuery/fulfilled`;
    const removeQueryResultType = `${prefix}/queries/removeQueryResult`;
    const executeMutationFulfilledType = `${prefix}/executeMutation/fulfilled`;
    const executeQueryResultPatchedType = `${prefix}/queries/queryResultPatched`;
    const getNormalizationType = `getNormalization`;
    
    
    type GetNormalizerAction = {
        type: getNormalizationType;
    };

    type QueryFulfilledAction = {
        type: executeQueryFulfilledType;
        payload: Data;
        meta: {
            arg: {
                queryCacheKey: string;
                originalArgs: unknown;
            };
        };
    };

    type QueryRemovedAction = {
        type: removeQueryResultType;
        payload: {
            queryCacheKey: string;
        };
    };

    type MutationFulfilledAction = {
        type: executeMutationFulfilledType;
        payload: Data;
        meta: {
            arg: {
                queryCacheKey: string;
                originalArgs: unknown;
                endpointName: string;
            };
        };
    };

    type QueryPatchedAction = {
        type: executeQueryResultPatchedType;
        payload: {
            queryCacheKey: string;
        };
    };

    type NormalizerAction =
        | GetNormalizerAction
        | QueryFulfilledAction
        | QueryRemovedAction
        | MutationFulfilledAction
        | QueryPatchedAction;

    type ActionType = NormalizerAction['type'];

    const allTypes: ReadonlyArray<ActionType> = [
        executeMutationFulfilledType,
        executeQueryFulfilledType,
        executeQueryResultPatchedType,
        removeQueryResultType,
        getNormalizationType,
    ];

    const isNormalizerAction = (action: unknown): action is NormalizerAction =>
        isAction(action) && (allTypes as ReadonlyArray<string>).includes(action.type);
    
    const normalizer = createNormalizer({
        ...normalizerConfig,
        // TODO: we wait for rtk-query maintainers to make this work
        structuralSharing: false,
    });

    const args: Record<string, unknown> = {};

    const middleware = store => next => action => {
        
        if (!isNormalizerAction(action)) {
            return next(action);
        }

        if (
            action.type === executeQueryResultPatchedType &&
            (normalizerConfig?.normalizeQuery?.(action.payload.queryCacheKey) ?? true)
        ) {
            const response = next(action);

            normalizer.setQuery(
                action.payload.queryCacheKey,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                store.getState()[api.reducerPath].queries[action.payload.queryCacheKey]
                    .data as Data,
            );

            return response;
        }

        if (action.type === getNormalizationType) {
            return {
                ...normalizer,
                setNormalizedData: (data: Data) => {
                    const queriesToUpdate = normalizer.getQueriesToUpdate(data);

                    queriesToUpdate.forEach(query => {
                        const endpoint = query.queryKey.split('(')[0];

                        store.dispatch(
                            // @ts-expect-error this is generic api, which is not typed
                            api.util.updateQueryData(
                                // @ts-expect-error this is generic api, which is not typed
                                endpoint,
                                args[query.queryKey],
                                () => query.data,
                            ),
                        );
                    });
                },
            };
        }

        if (
            action.type === executeQueryFulfilledType &&
            (normalizerConfig?.normalizeQuery?.(action.meta.arg.queryCacheKey) ??
                true)
        ) {
            normalizer.setQuery(action.meta.arg.queryCacheKey, action.payload);
            args[action.meta.arg.queryCacheKey] = action.meta.arg.originalArgs;
        } else if (action.type === removeQueryResultType) {
            normalizer.removeQuery(action.payload.queryCacheKey);
            delete args[action.payload.queryCacheKey];
        } else if (
            action.type === executeMutationFulfilledType &&
            (normalizerConfig?.normalizeMutation?.(action.meta.arg.endpointName) ??
                true)
        ) {
            const queriesToUpdate = normalizer.getQueriesToUpdate(action.payload);

            queriesToUpdate.forEach(query => {
                const endpoint = query.queryKey.split('(')[0];

                store.dispatch(
                    // @ts-expect-error ddd
                    api.util.updateQueryData(
                        // @ts-expect-error ddd
                        endpoint,
                        args[query.queryKey],
                        () => query.data,
                    ),
                );
            });
        }

        return next(action);
    };
    
    return [normalizer, middleware]
};
